import { StringLiteral } from 'src/models';
import envTyped from './env-replaced.json';

export type DeploymentType = StringLiteral | 'uspublic' | 'vois' | 'hellorache' | 'genesis';

/**
 * IMPORTANT: This list of envirornment variables should be synced with Dockerfile
 */
interface KnownEnvironmentVariables {
  NODE_ENV: 'production' | 'development' | 'test';
  TD_APP_VERSION?: string;
  TD_APP_DEPLOYMENT?: DeploymentType;
  TD_APP_SEGMENT_KEY?: string;
  TD_APP_HOME?: string;
  TD_APP_API_HOST?: string;
  TD_APP_INTEGRATIONS_API_URL?: string;
  TD_APP_SENTRY_DSN?: string;
  TD_APP_SENTRY_ENVIRONMENT?: string;
  TD_APP_SENTRY_DEBUG?: string;
  TD_APP_SENTRY_TRACES_SAMPLE_RATE?: string;
  TD_APP_AUTH_MODE?: 'v1' | 'v2' | StringLiteral;
  TD_APP_AUTH_V2_SPLIT_TEST_RATIO?: number | string;
  TD_APP_DISABLE_SOCKET?: 'true' | StringLiteral;
  TD_APP_DISABLE_ALL_SOCKET?: 'true' | StringLiteral;
  TD_APP_DISABLE_BILLING?: 'true' | StringLiteral;
  TD_APP_DISABLE_ONBOARDING?: 'true' | StringLiteral;
  TD_APP_DISABLE_INVITE?: 'true' | StringLiteral;
  TD_APP_DISABLE_DOWNLOAD?: 'true' | StringLiteral;
  TD_APP_DISABLE_SCREENCASTS?: 'true' | StringLiteral;
  TD_APP_ENABLE_SPLIT_TESTS?: 'true' | StringLiteral;
  TD_APP_ENABLE_ANALYTICS?: 'true' | StringLiteral;
  TD_APP_HTTP_TIMEOUT?: string;
  TD_APP_GOOGLE_CLIENT_ID?: string;
  TD_APP_DATA_INTEGRITY_APIURL?: string;
  TD_APP_DATA_INTEGRITY_APIKEY?: string;
  TD_APP_PWA_URL?: string;
  TD_APP_ANNOUNCE_KIT?: string;
  TD_APP_GROWTHBOOK_API_HOST?: string;
  TD_APP_GROWTHBOOK_CLIENT_KEY?: string;
  TD_APP_PADDLE_SANDBOX?: 'true' | StringLiteral;
  TD_APP_PADDLE_VENDOR?: string;
  TD_APP_PADDLE_CLIENT_TOKEN?: string;
  TD_APP_BILLING_API_URL?: string;
  TD_APP_ONBOARDING_URL?: string;
  TD_APP_GEO_INFO_API_URL?: string;
  TD_APP_HUBSPOT_KEY?: string;
  TD_APP_EMPLOYEE_PRIVACY_DISCLAIMER?: string;
  TD_APP_CONTRACTOR_PRIVACY_DISCLAIMER?: string;
  TD_APP_TDC_CHECK_EMAIL_END_POINT?: string;
  TD_APP_TRAINING_GUIDE_URL?: string;
  TD_APP_GOOGLE_IMPORT_CLIENT_ID?: string;
  TD_APP_HUBSPOT_FLOW_ID?: string;
  TD_APP_HUBSPOT_TOKEN_GENERATE?: string;
  TD_APP_TRIAL_WIDGET_REVENUE_HERO_ROUTER_ID?: string;
  TD_APP_TRIAL_WIDGET_REVENUE_HERO_SCHEDULE_ID?: string;
  TD_APP_UAR_UPSELL_REVENUE_HERO_ROUTER_ID?: string;
  TD_APP_UAR_UPSELL_REVENUE_HERO_SCHEDULE_ID?: string;
  TD_APP_AMPLITUDE_API_KEY?: string;
  TD_APP_DISCOUNT_ELIGIBILITY_API_URL?: string;
  TD_APP_QLIK_CLIENT_ID?: string;
  TD_APP_QLIK_OFFICE_VS_REMOTE_APP_ID?: string;
  TD_APP_QLIK_OFFICE_VS_REMOTE_SHEET_ID?: string;
  TD_APP_MIXPANEL_TOKEN?: string;
  TD_APP_HUBSPOT_PORTAL_ID?: string;
  TD_APP_HUBSPOT_REGION?: string;
  TD_APP_HUBSPOT_FORM_ID?: string;
  TD_APP_HUBSPOT_FEATURE_UPSELL_FORM_ID?: string;
  TD_APP_HUBSPOT_FEATURE_UPSELL_PORTAL_ID?: string;
  TD_APP_CELLO_PRODUCT_ID?: string;
  TD_APP_CELLO_SCRIPT_URL?: string;
  /** Root of ai-insights HTTP API (no trailing slash). Example: https://api.staging.timedoctor.com/api/2.0/ai-insights */
  TD_APP_AI_SUMMARY_API_URL?: string;
  /** Base URL of the NestJS Agent Gateway (no trailing slash). Empty disables Ask AI chat. */
  TD_APP_AGENT_GATEWAY_URL?: string;
  /** Display name for Copilot chrome (status fallbacks). Default Ask AI; set Athena when renaming. */
  TD_APP_ASSISTANT_DISPLAY_NAME?: string;
  /** React app origin, only needed when it differs from Angular's (e.g. local dev). Empty in staging/prod — /r is same-origin there. */
  TD_APP_REACT_URL?: string;
}

const env = envTyped as unknown as KnownEnvironmentVariables;

export interface Environment {
  version: string;
  deployment: string;
  production: boolean;
  hmr: boolean;
  test: boolean;
  baseHref: string;
  appName: string;
  appUrl: string;
  apiHost: string;
  apiUrl: string;
  httpTimeout: number;
  announceKitUrl: string | null;
  integrationsApiUrl: string;
  billingApiUrl: string;
  pwaAppUrl: string;
  onboardingUrl?: string;
  socketApiRoot: string;
  socketHost: string;
  defaultApiVersion: string;
  authMode: 'v1' | 'v2';
  authV2SplitTestRatio: number;
  useSocketApi: boolean;
  allowSocket: boolean;
  enableAnalytics: boolean;
  hubspotKey: string;
  sentryDsn: string;
  sentryEnvironment: string;
  sentryDebug: boolean;
  sentryTracesSampleRate: number;
  sentryRelease: string;
  googleClientId: string;
  tdcCheckEmailEndPoint: string;
  dataIntegrityApiUrl: string;
  dataIntegrityApiKey: string;
  enableSplitTest: boolean;
  growthbookApiHost: string;
  growthbookClientKey: string;
  geoInfoApiUrl: string;
  discountEligibilityApiUrl: string;
  env: KnownEnvironmentVariables;
  trialWidgetRevenueHeroRouterId: string;
  trialWidgetRevenueHeroScheduleId: string;
  uarUpsellRevenueHeroRouterId: string;
  uarUpsellRevenueHeroScheduleId: string;
  amplitudeApiKey: string;
  qlikClientId: string;
  qlikOfficeVsRemoteAppId: string;
  qlikOfficeVsRemoteSheetId: string;
  hubspotPortalId: string;
  hubspotRegion: string;
  hubspotFormId: string;
  hubspotFeatureUpsellFormId: string;
  hubspotFeatureUpsellPortalId: string;
  aiSummaryApiUrl: string;
  agentGatewayBaseUrl: string;
  /** Copilot assistant display name (Tempo → Athena via env). */
  assistantDisplayName: string;
  reactUrl: string;
}

const version = env.TD_APP_VERSION || '0.0.0';
const production = env.NODE_ENV === 'production';
const hmr = env.NODE_ENV === 'development';
const test = env.NODE_ENV === 'test';
const deployment = env.TD_APP_DEPLOYMENT || 'uspublic';

const appUrl = env.TD_APP_HOME || 'https://2.timedoctor.com';
const apiHost = env.TD_APP_API_HOST || 'https://api2.timedoctor.com';
const integrationsApiUrl = env.TD_APP_INTEGRATIONS_API_URL || `https://integrations.timedoctor.com`;
const onboardingUrl = env.TD_APP_ONBOARDING_URL;

const pwaAppUrl = env.TD_APP_PWA_URL || 'https://desktop.timedoctor.com';
const sentryDsn = env.TD_APP_SENTRY_DSN;
const sentryEnvironment = env.TD_APP_SENTRY_ENVIRONMENT;
const sentryDebug = env.TD_APP_SENTRY_DEBUG === 'true';
const sentryTracesSampleRate = env.TD_APP_SENTRY_TRACES_SAMPLE_RATE ? parseFloat(env.TD_APP_SENTRY_TRACES_SAMPLE_RATE) : 0.01;
const googleClientId = env.TD_APP_GOOGLE_CLIENT_ID;
const tdcCheckEmailEndPoint = env.TD_APP_TDC_CHECK_EMAIL_END_POINT;
const dataIntegrityApiUrl = env.TD_APP_DATA_INTEGRITY_APIURL;
const dataIntegrityApiKey = env.TD_APP_DATA_INTEGRITY_APIKEY;
const allowSocket = env.TD_APP_DISABLE_ALL_SOCKET !== 'true';
const enableSplitTest = env.TD_APP_ENABLE_SPLIT_TESTS === 'true';
const announceKitUrl = env.TD_APP_ANNOUNCE_KIT ? `https://announcekit.co/widgets/v2/${env.TD_APP_ANNOUNCE_KIT}` : null;
const geoInfoApiUrl = env.TD_APP_GEO_INFO_API_URL || 'https://td-website-mu.timedoctor.com/api/geo-info';
const discountEligibilityApiUrl = env.TD_APP_DISCOUNT_ELIGIBILITY_API_URL || 'https://td-website-mu.timedoctor.com/api/td-discount/is-allowed';
const hubspotKey = env.TD_APP_HUBSPOT_KEY;
const httpTimeout = env.TD_APP_HTTP_TIMEOUT ? parseInt(env.TD_APP_HTTP_TIMEOUT) * 1000 : 3 * 60 * 1000;
const amplitudeApiKey = env.TD_APP_AMPLITUDE_API_KEY;
const qlikClientId = env.TD_APP_QLIK_CLIENT_ID;
const qlikOfficeVsRemoteAppId = env.TD_APP_QLIK_OFFICE_VS_REMOTE_APP_ID;
const qlikOfficeVsRemoteSheetId = env.TD_APP_QLIK_OFFICE_VS_REMOTE_SHEET_ID;
const reactUrl = env.TD_APP_REACT_URL || '';

// ToDo: Define the proper billing API URL once it is available
const billingApiUrl = env.TD_APP_BILLING_API_URL || `https://billing.timedoctor.com/v1`;

// revenue hero
const trialWidgetRevenueHeroRouterId = env.TD_APP_TRIAL_WIDGET_REVENUE_HERO_ROUTER_ID;
const trialWidgetRevenueHeroScheduleId = env.TD_APP_TRIAL_WIDGET_REVENUE_HERO_SCHEDULE_ID;

const uarUpsellRevenueHeroRouterId = env.TD_APP_UAR_UPSELL_REVENUE_HERO_ROUTER_ID;
const uarUpsellRevenueHeroScheduleId = env.TD_APP_UAR_UPSELL_REVENUE_HERO_SCHEDULE_ID;
const hubspotPortalId = env.TD_APP_HUBSPOT_PORTAL_ID;
const hubspotRegion = env.TD_APP_HUBSPOT_REGION || 'na1';
const hubspotFormId = env.TD_APP_HUBSPOT_FORM_ID;
const hubspotFeatureUpsellFormId = env.TD_APP_HUBSPOT_FEATURE_UPSELL_FORM_ID;
const hubspotFeatureUpsellPortalId = env.TD_APP_HUBSPOT_FEATURE_UPSELL_PORTAL_ID;
const aiSummaryApiUrl = env.TD_APP_AI_SUMMARY_API_URL || '';
const agentGatewayBaseUrl = env.TD_APP_AGENT_GATEWAY_URL || 'https://integrations.staging.timedoctor.com/v1/agent-gateway/';
const assistantDisplayName = (env.TD_APP_ASSISTANT_DISPLAY_NAME || 'Ask AI').trim() || 'Ask AI';

export const environment: Environment = {
  version,
  deployment,
  hmr,
  production,
  test,
  httpTimeout,
  enableAnalytics: env.TD_APP_ENABLE_ANALYTICS === 'true' || (!!env.TD_APP_SEGMENT_KEY && production),
  hubspotKey,
  authMode: env.TD_APP_AUTH_MODE === 'v2' ? 'v2' : 'v1',
  authV2SplitTestRatio: env.TD_APP_AUTH_V2_SPLIT_TEST_RATIO ? Number(env.TD_APP_AUTH_V2_SPLIT_TEST_RATIO) : 0,
  useSocketApi: allowSocket && production && env.TD_APP_DISABLE_SOCKET !== 'true',
  allowSocket,
  baseHref: '/',
  appName: 'timedoctor2-web',
  appUrl,
  apiHost,
  apiUrl: `${apiHost}/api/{version}`,
  billingApiUrl,
  pwaAppUrl,
  announceKitUrl,
  onboardingUrl,
  integrationsApiUrl,
  socketApiRoot: '/api/{version}',
  defaultApiVersion: '1.0',
  socketHost: apiHost,
  sentryDsn,
  sentryEnvironment,
  sentryDebug,
  sentryTracesSampleRate,
  sentryRelease: version,
  env,
  googleClientId,
  tdcCheckEmailEndPoint,
  dataIntegrityApiUrl,
  dataIntegrityApiKey,
  enableSplitTest,
  growthbookApiHost: env.TD_APP_GROWTHBOOK_API_HOST,
  growthbookClientKey: env.TD_APP_GROWTHBOOK_CLIENT_KEY,
  geoInfoApiUrl,
  discountEligibilityApiUrl,
  trialWidgetRevenueHeroRouterId,
  trialWidgetRevenueHeroScheduleId,
  uarUpsellRevenueHeroRouterId,
  uarUpsellRevenueHeroScheduleId,
  amplitudeApiKey,
  qlikClientId,
  qlikOfficeVsRemoteAppId,
  qlikOfficeVsRemoteSheetId,
  hubspotPortalId,
  hubspotRegion,
  hubspotFormId,
  hubspotFeatureUpsellFormId,
  hubspotFeatureUpsellPortalId,
  aiSummaryApiUrl,
  agentGatewayBaseUrl,
  assistantDisplayName,
  reactUrl,
};
